import type { CreatedAPIClientDTO } from "@pentacode/core";
import { css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Dialog } from "../dialog";

/** Shows a freshly created client's credentials; the secret is never displayed again afterwards. */
@customElement("ptc-api-client-secret-dialog")
export class APIClientSecretDialog extends Dialog<CreatedAPIClientDTO, void> {
    preventDismiss = true;

    @state()
    private _copied = false;

    private async _copy() {
        if (!this.input) return;
        await navigator.clipboard.writeText(`clientId: ${this.input.id}\nclientSecret: ${this.input.secret}`);
        this._copied = true;
    }

    async show(input: CreatedAPIClientDTO) {
        this._copied = false;
        return super.show(input);
    }

    // Once hidden, the secret must not linger in the DOM or memory of the closed dialog.
    updated(changed: Map<string, unknown>) {
        super.updated(changed);
        if (changed.get("isShowing") === true && !this.isShowing && this.input) {
            this.input = undefined as unknown as CreatedAPIClientDTO;
        }
    }

    static styles = [
        ...Dialog.styles,
        css`
            .inner {
                --dialog-max-width: 536px;
                --dialog-width: 536px;
            }

            /* A 64-character hex secret has to fit on one line without scrolling. */
            input[readonly] {
                width: 100%;
                font-family: var(--font-mono, monospace);
                font-size: var(--font-size-small);
                letter-spacing: 0;
            }
        `,
    ];

    renderContent() {
        const client = this.input;
        if (!client) return html``;

        return html`
            <div class="vertical layout">
                <div class="margined text-centering big">API-Client erstellt</div>

                <div class="padded">
                    <div class="margined medium">
                        Partner <strong>${client.name}</strong> wurde erfolgreich erstellt. 🎉
                    </div>
                    <div class="margined bold">Achtung:</div>
                    <div class="margined">
                        Das Client-Secret wird nur dieses eine Mal angezeigt. Speichern Sie beide Werte sicher, bevor
                        Sie den Dialog schließen.
                    </div>

                    <div class="field">
                        <label>Client-ID</label>
                        <input readonly .value=${client.id} />
                    </div>

                    <div class="field">
                        <label>Client-Secret</label>
                        <input readonly .value=${client.secret} />
                    </div>

                    <button class="transparent fill-horizontally" type="button" @click=${this._copy}>
                        <ptc-ui-icon
                            name=${this._copied ? "icon.status.success" : "icon.action.copy-to-clipboard"}
                        ></ptc-ui-icon>
                        ${this._copied ? "Kopiert" : "Client-ID und Client-Secret kopieren"}
                    </button>
                </div>

                <div class="padded horizontal spacing evenly stretching layout">
                    <button class="primary" type="button" @click=${() => this.done()}>Fertig</button>
                </div>
            </div>
        `;
    }
}
